// client/src/pages/seo/DigitalesBestellsystemGastronomieDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Benötigt Ihr Restaurant ein digitales Bestellsystem?",
  },
  {
    id: "definition",
    label: "Was ist ein digitales Bestellsystem für Restaurants?",
  },
  {
    id: "types",
    label: "Welche Arten digitaler Bestellung gibt es?",
  },
  {
    id: "expectation",
    label: "Warum digitales Bestellen für Gäste normal wird",
  },
  {
    id: "direct",
    label: "Direktbestellung oder Drittanbieter-Plattformen: Was ist besser?",
  },
  {
    id: "qr-ordering",
    label: "QR-Code-Bestellung: direkt vom Tisch bestellen",
  },
  {
    id: "workflow",
    label: "Wie ein guter digitaler Bestellablauf aussehen sollte",
  },
  {
    id: "payment",
    label: "Digitale Zahlung und kontaktlose Abläufe",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler bei digitalen Bestellsystemen",
  },
  {
    id: "bestplace",
    label: "Wie Best Place beim digitalen Bestellen helfen kann",
  },
  {
    id: "faq",
    label: "FAQ zu digitalen Bestellsystemen",
  },
];

const checklist = [
  "Gäste fragen häufig nach Abholung oder Vorbestellung.",
  "Ihr Restaurant erhält Bestellungen über Telefon, Nachrichten oder soziale Medien.",
  "Sie möchten Bestellungen strukturierter erfassen.",
  "Sie möchten Speisekarte, QR-Code und Bestellung besser verbinden.",
  "Sie möchten weniger Missverständnisse bei Namen, Preisen oder Produkten.",
  "Sie möchten später Abholung, Takeaway oder Bestellung am Tisch digital anbieten.",
  "Sie möchten Zusatzangebote wie Getränke, Desserts oder Menüs sichtbarer machen.",
  "Der Bestellablauf soll auf Smartphones einfacher sein.",
  "Sie möchten weniger ausschließlich von Drittanbieter-Plattformen abhängig sein.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Ein digitales Bestellsystem ist vielleicht noch nicht dringend",
      text:
        "Wenn Bestellungen bei Ihnen selten oder sehr einfach sind, kann eine digitale Speisekarte mit klaren Informationen zuerst der bessere Schritt sein.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Ein digitaler Bestellablauf lohnt sich zum Testen",
      text:
        "Ihr Restaurant könnte davon profitieren, Bestellungen klarer zu erfassen und Speisekarte, QR-Code sowie die nächste Aktion besser zu verbinden.",
      tone: "medium",
    };
  }

  return {
    title: "Digitales Bestellen kann ein wichtiger nächster Schritt werden",
    text:
      "Mehrere Punkte sprechen dafür, dass digitales Bestellen Ihrem Restaurant helfen kann: weniger Chaos, klarere Bestelldaten, bessere mobile Nutzung und mehr Kontrolle.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalesBestellsystemGastronomieDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Digitales Bestellsystem für Gastronomie | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/digital-ordering-system-for-restaurants",
          de: "/de/digitales-bestellsystem-gastronomie",
          no: "/no/digitalt-bestillingssystem-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digitales Bestellsystem für Gastronomie: von der Speisekarte zur
              Bestellung
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Ein digitales Bestellsystem beginnt nicht mit der Zahlung. Es
                beginnt dort, wo Gäste Ihr Angebot verstehen: bei der
                Speisekarte, beim QR-Code, bei der Smartphone-Erfahrung und bei
                einem klaren nächsten Schritt.
              </p>

              <p className="seoHero__text">
                Für Restaurants kann digitales Bestellen helfen, Bestellungen
                klarer zu erfassen, Missverständnisse zu reduzieren und
                Zusatzangebote wie Getränke, Desserts, Menüs, Abholung oder
                Takeaway sichtbarer zu machen.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Benötigt Ihr Restaurant ein digitales Bestellsystem?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Nicht jedes Restaurant braucht sofort
              digitales Bestellen. Viele Restaurants profitieren aber früher als
              gedacht von einem klareren Ablauf von der Speisekarte bis zur
              Bestellung.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was ist ein digitales Bestellsystem für Restaurants?</h2>

          <p>
            Ein digitales Bestellsystem ist ein Ablauf, mit dem Gäste Speisen
            oder Getränke über eine Website, digitale Speisekarte, einen QR-Code
            oder eine mobile Oberfläche auswählen und eine Bestellung oder
            strukturierte Anfrage senden können.
          </p>

          <p>
            Je nach Restaurant kann das Abholung, Takeaway, Lieferung,
            Vorbestellung oder Bestellung am Tisch bedeuten. Entscheidend ist
            nicht nur die Technik. Gäste sollten klar verstehen, was verfügbar
            ist, was es kostet und wie sie bestellen können.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Digitale Speisekarte.</li>
            <li className="text__seo-li">Zugang per QR-Code.</li>
            <li className="text__seo-li">Produktauswahl.</li>
            <li className="text__seo-li">Warenkorb oder Anfrage.</li>
            <li className="text__seo-li">
              Abholung, Lieferung oder Bestellung am Tisch.
            </li>
            <li className="text__seo-li">Optionale digitale Zahlung.</li>
            <li className="text__seo-li">
              Strukturierte Bestelldaten für das Restaurant.
            </li>
          </ul>

          <h3>Bestellen sollte mit einer klaren Speisekarte beginnen</h3>

          <p>
            Wenn die Speisekarte unübersichtlich ist, wird auch der
            Bestellprozess unübersichtlich. Ein gutes digitales Bestellsystem
            braucht eine saubere Grundlage: Produkte, Preise, Verfügbarkeit,
            Beschreibungen und einen einfachen mobilen Ablauf.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Arten digitaler Bestellung gibt es?</h2>

            <p>
              Nicht jedes Restaurant braucht sofort dasselbe Bestellsystem. Ein
              Café, ein Takeaway-Betrieb, ein Hotelrestaurant und eine Pizzeria
              haben unterschiedliche Abläufe.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Bestellarten</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Takeaway-Bestellung:</strong> Gäste bestellen online
                    und holen später ab.
                  </li>
                  <li className="text__seo-li">
                    <strong>Lieferbestellung:</strong> Gäste bestellen online
                    und erhalten eine Lieferung.
                  </li>
                  <li className="text__seo-li">
                    <strong>Vorbestellung:</strong> Gäste wählen Zeit und
                    Produkte vor dem Besuch aus.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Flexible erste Schritte</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>QR-Code-Bestellung:</strong> Gäste scannen den Code
                    und bestellen direkt vom Tisch.
                  </li>
                  <li className="text__seo-li">
                    <strong>Strukturierte Anfrage:</strong> Das Restaurant
                    erhält eine Bestellanfrage statt sofortiger Automatisierung.
                  </li>
                  <li className="text__seo-li">
                    <strong>Speisekarte mit Bestelloption:</strong> Die digitale
                    Speisekarte wird zum Einstiegspunkt.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Für viele Restaurants ist der beste Start nicht „alles auf
              einmal“. Besser ist ein klarer erster Schritt: digitale
              Speisekarte, QR-Code, verständliche Produkte und später ein
              Bestellablauf.
            </p>
          </div>
        </section>

        <section id="expectation" className="acquaintance">
          <h2>Warum digitales Bestellen für Gäste normal wird</h2>

          <p>
            Gäste sind daran gewöhnt, Dinge auf dem Smartphone zu erledigen:
            Speisekarten ansehen, Tische reservieren, bestellen oder bezahlen.
            Das bedeutet nicht, dass jedes Restaurant alles automatisieren muss.
            Es bedeutet aber, dass ein einfacher digitaler Weg zunehmend
            erwartet wird.
          </p>

          <p>
            Für Best Place ist die wichtige Idee: Digitales Bestellen sollte
            nicht isoliert sein. Es sollte dort anschließen, wo der Gast bereits
            ist — bei der Speisekarte, beim QR-Code, auf der Restaurantseite oder
            im Reservierungsablauf.
          </p>

          <h3>Digitales Bestellen sollte sich wie der nächste Schritt anfühlen</h3>

          <p>
            Zuerst möchte ein Gast das Angebot verstehen. Wenn der nächste
            Schritt klar ist, kann er eine Bestellung senden, Abholung anfragen,
            einen Tisch reservieren oder weitere Informationen einholen.
          </p>

          <VisualIdea>
            Ein Gast öffnet eine digitale Speisekarte auf dem Smartphone, prüft
            Gerichte und Preise und geht dann natürlich zu einem einfachen
            Bestell- oder Anfrage-Button weiter.
          </VisualIdea>
        </section>

        <section id="direct" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Direktbestellung oder Drittanbieter-Plattformen: Was ist besser?
            </h2>

            <p>
              Drittanbieter-Plattformen können Reichweite bringen und für viele
              Restaurants nützlich sein. Gleichzeitig ist es wertvoll, wenn ein
              Restaurant zusätzlich einen eigenen digitalen Weg aufbaut.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Drittanbieter-Plattform</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Kann zusätzliche Reichweite bringen.
                  </li>
                  <li className="text__seo-li">
                    Gäste kennen die Plattform möglicherweise bereits.
                  </li>
                  <li className="text__seo-li">
                    Das Restaurant ist von Regeln und Layout der Plattform
                    abhängig.
                  </li>
                  <li className="text__seo-li">
                    Die Kundenbeziehung gehört nicht vollständig dem Restaurant.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Eigener digitaler Bestellweg</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Mehr Kontrolle über Speisekarte und Darstellung.
                  </li>
                  <li className="text__seo-li">
                    Direkter Link von Website oder QR-Code.
                  </li>
                  <li className="text__seo-li">
                    Bessere Verbindung mit der Restaurantmarke.
                  </li>
                  <li className="text__seo-li">
                    Kann Schritt für Schritt erweitert werden.
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Die beste Strategie muss nicht entweder-oder sein. Ein Restaurant
              kann Plattformen nutzen und trotzdem eine eigene digitale Struktur
              aufbauen.
            </p>
          </div>
        </section>

        <section id="qr-ordering" className="acquaintance">
          <h2>QR-Code-Bestellung: direkt vom Tisch bestellen</h2>

          <p>
            QR-Code-Bestellung bedeutet, dass Gäste am Tisch einen QR-Code
            scannen, die digitale Speisekarte öffnen und von dort eine Bestellung
            oder Anfrage starten.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">QR-Code scannen.</li>
            <li className="text__seo-li">Speisekarte öffnen.</li>
            <li className="text__seo-li">Produkte auswählen.</li>
            <li className="text__seo-li">Bestellung senden.</li>
            <li className="text__seo-li">Restaurant bearbeitet sie.</li>
          </ol>

          <p>
            Das kann besonders für Konzepte interessant sein, bei denen Gäste
            häufig nachbestellen möchten: Getränke, Desserts, Snacks, Menüs oder
            eine weitere Runde.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/qr-code-speisekarte">
                  QR-Code-Speisekarte für Restaurants
                </Link>
              </p>
            </div>

            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/digitale-speisekarte">
                  Digitale Speisekarte für Restaurants
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wie ein guter digitaler Bestellablauf aussehen sollte</h2>

            <p>
              Ein gutes Bestellsystem ist nicht nur ein Button. Es ist ein klarer
              Ablauf, der Fehler reduziert und Gästen Sicherheit gibt.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                Gast öffnet die Speisekarte über Website oder QR-Code.
              </li>
              <li className="text__seo-li">
                Gast sieht Kategorien, Preise, Fotos und Verfügbarkeit.
              </li>
              <li className="text__seo-li">Gast wählt Produkte aus.</li>
              <li className="text__seo-li">
                Gast sieht Zusammenfassung und Gesamtpreis.
              </li>
              <li className="text__seo-li">
                Gast wählt Abholung, Tischbestellung oder Anfrage.
              </li>
              <li className="text__seo-li">
                Restaurant erhält klare Bestelldaten.
              </li>
              <li className="text__seo-li">
                Team bestätigt oder bearbeitet die Bestellung.
              </li>
            </ol>

            <p>
              Je klarer dieser Ablauf ist, desto weniger Fragen entstehen.
              Digitales Bestellen sollte nicht komplizierter sein als ein
              Telefonanruf — es sollte klarer sein.
            </p>
          </div>
        </section>

        <section id="payment" className="acquaintance">
          <h2>Digitale Zahlung und kontaktlose Abläufe</h2>

          <p>
            Nicht jedes Restaurant braucht sofort digitale Zahlung. Aber
            Bestellung, Abholung und Zahlung gehören langfristig zusammen. Wenn
            ein Gast digital bestellt, erwartet er oft einen klaren nächsten
            Schritt für Zahlung oder Abholung.
          </p>

          <p>
            Für eine erste Version kann das Restaurant mit strukturierten
            Anfragen oder Vorbestellungen starten. Später können Online-Zahlung,
            QR-Zahlung, Zahlung bei Abholung oder kontaktlose Zahlung ergänzt
            werden — je nach Restaurantkonzept.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">
              Strukturierte Anfrage ohne Zahlung.
            </li>
            <li className="text__seo-li">
              Abholbestellung mit Zahlung vor Ort.
            </li>
            <li className="text__seo-li">
              Vorbestellung mit späterer Bestätigung.
            </li>
            <li className="text__seo-li">
              Digitale Zahlung als späterer Schritt.
            </li>
            <li className="text__seo-li">
              Kontaktlose Zahlung für einen reibungsloseren Gästeablauf.
            </li>
          </ul>

          <h3>Zahlung muss nicht der erste Schritt sein</h3>

          <p>
            Ein Restaurant kann zuerst testen, ob Gäste die digitale
            Speisekarte, Produktauswahl und den Bestellablauf verstehen. Zahlung
            kann ergänzt werden, sobald der grundlegende Prozess klar ist.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Häufige Fehler bei digitalen Bestellsystemen</h2>

            <p>
              Digitales Bestellen hilft nur, wenn der Ablauf für Gäste klar und
              für das Team gut zu bewältigen ist. Wenn ein Restaurant mit zu viel
              Komplexität startet, kann das System neue Probleme schaffen, statt
              alte zu lösen.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Bestellung wird hinzugefügt, bevor die Speisekarte klar ist.
                  </li>
                  <li className="text__seo-li">
                    Es gibt zu viele unnötige Schritte.
                  </li>
                  <li className="text__seo-li">
                    Ausverkaufte Artikel bleiben verfügbar.
                  </li>
                  <li className="text__seo-li">
                    Angaben zu Abholung oder Lieferung sind unklar.
                  </li>
                  <li className="text__seo-li">
                    Der Gast erhält keine klare Bestätigung.
                  </li>
                  <li className="text__seo-li">
                    Das Team wird nicht in den Ablauf einbezogen.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zuerst die digitale Speisekarte als Grundlage aufbauen.
                  </li>
                  <li className="text__seo-li">
                    Den Bestellablauf kurz halten.
                  </li>
                  <li className="text__seo-li">
                    Nicht verfügbare Artikel sofort ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Abholung, Tischbestellung oder Anfrage klar erklären.
                  </li>
                  <li className="text__seo-li">
                    Nach dem Absenden eine klare Bestätigung anzeigen.
                  </li>
                  <li className="text__seo-li">
                    Das Team im Umgang mit Anfragen schulen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zu digitalen Bestellsystemen</h2>

          <div className="seoFaq">
            <details>
              <summary>
                Braucht jedes Restaurant ein digitales Bestellsystem?
              </summary>
              <p>
                Nein. Wenn Sie keine Abholung, Lieferung oder Bestellung am
                Tisch anbieten, ist eine digitale Speisekarte möglicherweise der
                wichtigere erste Schritt.
              </p>
            </details>

            <details>
              <summary>Ist digitales Bestellen nur für Lieferung gedacht?</summary>
              <p>
                Nein. Es kann auch für Abholung, Vorbestellung oder Bestellung
                am Tisch genutzt werden.
              </p>
            </details>

            <details>
              <summary>Kann QR-Code-Bestellung den Service ersetzen?</summary>
              <p>
                Nicht unbedingt. In vielen Restaurants unterstützt sie den
                Service, zum Beispiel bei zusätzlichen Getränken, Desserts oder
                Nachbestellungen.
              </p>
            </details>

            <details>
              <summary>
                Ist Direktbestellung besser als eine Plattform?
              </summary>
              <p>
                Nicht immer. Plattformen können Reichweite bringen. Ein eigener
                digitaler Weg gibt mehr Kontrolle über Marke, Speisekarte und
                Gästebeziehung.
              </p>
            </details>

            <details>
              <summary>
                Muss Zahlung sofort integriert werden?
              </summary>
              <p>
                Nein. Manche Restaurants starten mit strukturierten Anfragen
                oder Vorbestellungen und ergänzen digitale Zahlung später.
              </p>
            </details>

            <details>
              <summary>Was sollte zuerst umgesetzt werden?</summary>
              <p>
                Eine klare digitale Speisekarte ist die Grundlage. Ohne gute
                Menüstruktur wird auch das Bestellen schwieriger.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="business"
          exclude="/de/digitales-bestellsystem-gastronomie"
        />
      </article>
    </main>
  );
}